import { Box, Text } from "@vinomofo/components";
import { useCart } from "../../hooks/use-cart";
import styled from "styled-components";
import config from "../../../config.json";
import { capitalize } from "../../utils/string-helpers";

const ProgressContainer = styled(Box)`
  margin: 0;
  padding: 10px 20px 10px 20px;
`;

const ProgressBarWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 5px;
  padding: 0 20px;
`;

const ProgressBarContainer = styled(Box)`
  flex: 1;
  position: relative;
  height: 10px;
  background-color: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressFill = styled(Box)<{ percentage: number }>`
  height: 100%;
  width: ${({ percentage }) => Math.min(percentage, 100)}%;
  background: linear-gradient(90deg, #a6a6a6 0%, #414141 100%);
  border-radius: 6px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const TargetAmount = styled(Text)`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.heading};
  white-space: nowrap;
  padding: 5px;
  border-radius: 6px;
`;

const ProgressText = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.heading};
  text-align: center;
  font-weight: 500;

  strong {
    color: ${({ theme }) => theme.colors.mofoPrimary};
    font-weight: 700;
  }
`;

const SegmentProgressBarWrapper = styled(Box)`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
  padding: 0 20px;
`;

const ProgressSegment = styled(Box)<{ isCompleted: boolean }>`
  flex: 1;
  height: 8px;
  background-color: ${({ isCompleted }) =>
    isCompleted ? "#414141" : "#A6A6A6"};
  border-radius: 4px;
  transition: background-color 0.3s ease;
`;

export const FreeShippingProgressBar = (): JSX.Element => {
  const { cart } = useCart();

  if (!cart?.lineItems?.length) {
    return <></>;
  }

  const currentMarket = process.env.GATSBY_VM_MARKET || "AU";
  const currentCases = cart.cartQuantity || 0;
  const currentAmount = cart.cartAmount || 0;
  const { isFreeShipping, isGiftCardOnly } = cart;
  const isQuantityBased = currentMarket === "AU";
  const threshold = isQuantityBased
    ? parseInt(config[currentMarket].MIN_ITEMS_FOR_FREE_SHIPPING)
    : parseInt(config[currentMarket].MIN_VALUE_FOR_FREE_SHIPPING);

  const currentProgress = isQuantityBased ? currentCases : currentAmount;
  const progressPercentage = (currentProgress / threshold) * 100;

  const promoMessage = cart.shippingPromoMessage;
  if (currentMarket === "SG") {
    const formatter = new Intl.NumberFormat("en-SG", {
      style: "currency",
      currency: "SGD",
    });

    if (isFreeShipping || isGiftCardOnly || progressPercentage >= 100) {
      return (
        <ProgressContainer>
          {!isFreeShipping && !isGiftCardOnly ? (
            <ProgressBarWrapper>
              <ProgressBarContainer>
                <ProgressFill percentage={progressPercentage} />
              </ProgressBarContainer>
              <TargetAmount>{formatter.format(threshold)}</TargetAmount>
            </ProgressBarWrapper>
          ) : (
            <></>
          )}
          <ProgressText>
            You&apos;ve qualified for <strong>FREE SHIPPING!</strong>
          </ProgressText>
        </ProgressContainer>
      );
    }

    return (
      <ProgressContainer>
        <ProgressBarWrapper>
          <ProgressBarContainer>
            <ProgressFill percentage={progressPercentage} />
          </ProgressBarContainer>
          <TargetAmount>{formatter.format(threshold)}</TargetAmount>
        </ProgressBarWrapper>
        <ProgressText>
          {promoMessage ? (
            <>
              {capitalize(promoMessage)} for <strong>FREE SHIPPING</strong>
            </>
          ) : (
            <>
              Spend{" "}
              <strong>{formatter.format(threshold - currentAmount)}</strong>{" "}
              more for <strong>FREE SHIPPING</strong>
            </>
          )}
        </ProgressText>
      </ProgressContainer>
    );
  }

  if (isFreeShipping || isGiftCardOnly || progressPercentage >= 100) {
    const progressSegments = Array.from({ length: threshold }, (_, index) => (
      <ProgressSegment key={index} isCompleted={true} />
    ));

    return (
      <ProgressContainer>
        {!isFreeShipping && !isGiftCardOnly ? (
          <SegmentProgressBarWrapper>
            {progressSegments}
          </SegmentProgressBarWrapper>
        ) : (
          <></>
        )}
        <ProgressText>
          You&apos;ve qualified for <strong>FREE SHIPPING</strong>
        </ProgressText>
      </ProgressContainer>
    );
  }

  const progressSegments = Array.from({ length: threshold }, (_, index) => {
    const segmentProgress = currentCases >= index + 1;
    return <ProgressSegment key={index} isCompleted={segmentProgress} />;
  });

  if (promoMessage) {
    return (
      <ProgressContainer>
        <SegmentProgressBarWrapper>
          {progressSegments}
        </SegmentProgressBarWrapper>
        <ProgressText>
          {capitalize(promoMessage)} for <strong>FREE SHIPPING</strong>
        </ProgressText>
      </ProgressContainer>
    );
  }

  return (
    <ProgressContainer>
      <SegmentProgressBarWrapper>{progressSegments}</SegmentProgressBarWrapper>
      <ProgressText>
        You&apos;ve qualified for <strong>FREE SHIPPING</strong>
      </ProgressText>
    </ProgressContainer>
  );
};
